import { RESOURCE_TYPE_FILE, RESOURCE_TYPE_MERGED_FILE } from "@Features/transmittals/hooks/transmittal.types";
import type { FileDetailsRecord } from "@ServerTypes/fileOperations/fileOperations.schemas";
import { isNonZero } from "@Helpers/numberCondition";
import { constituentsOf, versionOf } from "@Reducers/fileVersion";

/**
 * Expands a viewer's `fileVersionId` into the list of fileVersionIds whose model-properties we want to load.
 *   RESOURCE_TYPE_FILE         → [fileVersionId]
 *   RESOURCE_TYPE_MERGED_FILE  → the constituent versions of that version whose files are not deleted
 * Same branching the viewer uses when dispatching the initial `getModelProperties`.
 */
export function resolveFileVersionIds(
  // The `filesData` redux slice, keyed by fileVersionId (see reducers/fileVersion.ts).
  filesData: Record<string, FileDetailsRecord> | undefined,
  // Callers pass the viewer's currently-open file version, which is absent when no file is
  // mounted; the `!fileVersionId` guard below has always handled that.
  fileVersionId: string | undefined,
): string[] {
  if (!fileVersionId) return [];
  const entry = filesData?.[fileVersionId];
  if (!entry) return [fileVersionId];

  if (entry.resourceType === RESOURCE_TYPE_FILE) {
    return [fileVersionId];
  }
  if (entry.resourceType === RESOURCE_TYPE_MERGED_FILE) {
    return constituentsOf(entry, fileVersionId)
      .filter(({ file }) => !isNonZero(file.isDeleted))
      .map(({ version }) => version._id);
  }
  return [fileVersionId];
}

/**
 * False when `id` (the viewer's file itself, or one of its constituents) has no model-tree archive:
 * the converter extracts no properties for its format. A version not in `filesData` counts as having one.
 */
export function hasModelTreeKey(
  filesData: Record<string, FileDetailsRecord> | undefined,
  fileVersionId: string,
  id: string,
): boolean {
  const entry = filesData?.[fileVersionId];
  const version =
    id === fileVersionId
      ? versionOf(entry, fileVersionId)
      : constituentsOf(entry, fileVersionId).find((constituent) => constituent.version._id === id)?.version;
  return version === undefined || Boolean(version.modelTreeKey);
}
